/**
 * Preferences Type Definitions
 *
 * Shared type definitions for user preferences across all MFEs.
 * This ensures consistent preference handling throughout the application.
 */

/**
 * Notification flags for different notification types
 */
export interface NotificationFlags {
  workingOrder: boolean
  fill: boolean
  canceledOrder: boolean
  rejectedOrder: boolean
  alert: boolean
  chat: boolean
  notification: boolean
  stopFill?: boolean  // Audio only
  rejectedModification?: boolean  // Audio only
}

/**
 * Hotkey command binding
 */
export interface HotkeyCommands {
  [commandName: string]: string  // e.g., { "buyMarket": "Ctrl+B" }
}

/**
 * Ticker notification settings
 */
export interface TickerSettings {
  enabled: boolean
  timeZone: 'EST' | 'CST' | 'PST' | 'GMT' | 'UTC'
}

/**
 * Complete preferences data structure
 * This is the shape stored in localStorage and managed by PreferencesManager
 */
export interface PreferencesData {
  // Appearance
  theme: 'dark' | 'light' | 'auto'

  // Notifications
  ticker: TickerSettings
  summaryEmail: boolean
  newsNotifications: boolean
  orderNotifications: NotificationFlags
  pushNotifications: NotificationFlags
  audioNotifications: NotificationFlags

  // Confirmations
  confirmations: {
    orders: boolean
    closeModule: boolean
    alerts: boolean
  }

  // Trading
  autoRollContracts: boolean

  // Privacy
  thirdPartyDataSharing: boolean

  // Hotkeys
  hotkeys: {
    enabled: boolean
    commands: HotkeyCommands
  }
}

/**
 * Default notification values (from legacy)
 */
export const DEFAULT_NOTIFICATIONS: {
  order: NotificationFlags
  push: NotificationFlags
  audio: NotificationFlags
} = {
  order: {
    workingOrder: true,
    fill: true,
    canceledOrder: true,
    rejectedOrder: true,
    alert: true,
    chat: true,
    notification: true,
    stopFill: true,
    rejectedModification: true,
  },
  push: {
    workingOrder: true,
    fill: true,
    canceledOrder: true,
    rejectedOrder: true,
    alert: true,
    chat: true,
    notification: true,
  },
  audio: {
    workingOrder: false,
    fill: true,
    canceledOrder: false,
    rejectedOrder: true,
    alert: true,
    chat: true,
    notification: true,
    stopFill: true,
    rejectedModification: false,
  }
}
